import { MouseEvent } from "react";
import { useRouter } from "next/router";
import { PopoverBody, PopoverArrow } from "@chakra-ui/react";
import { keyBy } from "lodash";
import { gql } from "__generated__/gql";
import { useQuery } from "utils/apollo/hooks";
import { useAdminOrBrandContext } from "utils/apollo/hooks/adminOrBrand";
import LabeledValueTable, {
  LabeledValueRow,
} from "components/Shared/LabeledValueTable";
import { formatUpc } from "components/Shared/Forms/UpcInput";
import Link from "components/Shared/Link";
import { selectedProductLine, selectedSku } from "utils/links";
import { useAuth } from "utils/auth";
import { assertBrandId } from "utils/assertions";
import { asyncVoid } from "utils/asyncVoidHandler";
import {
  SKU_COMPONENT_TYPE_LABELS,
  SKU_COMPONENT_TYPES,
} from "components/Products/SkuPanel/SkuComponents/utils";
import CopyableCode from "components/Shared/CopyableCode";
import { formatUnitNetWeight } from "components/Products/utils";
import { formatInteger } from "utils/formatting";
import { useCurrentBrandId } from "utils/currentUserHooks";

const query = gql(/* GraphQL */ `
  query SkuPreviewPopover($skuId: UUID!) {
    sku(id: $skuId) {
      id
      name
      fullNameShort
      internalSkuId
      unitNetWeight
      unitNetWeightUnit
      productLine {
        id
        name
        brandId
        wholesaleCaseQuantity
        innerCaseQuantity
        brand {
          id
          name
        }
      }
      skuComponentsList {
        id
        skuComponentType
        upc
      }
    }
  }
`);

type Props = {
  isAdmin?: boolean;
  skuId: UUID;
};

export default function SkuPreviewPopoverContent({ isAdmin, skuId }: Props) {
  const currentBrandId = useCurrentBrandId();

  const [context, isReady] = useAdminOrBrandContext(isAdmin, {
    isDisabledForParentBrand: true,
  });

  const { data, loading } = useQuery(query, {
    skip: !isReady,
    variables: { skuId },
    returnPartialData: true,
    context,
  });

  const { sku } = data || {};
  const router = useRouter();
  const { setCurrentBrandId } = useAuth();

  const handleLinkClick = (evt: MouseEvent<HTMLAnchorElement>) => {
    if (!isAdmin) return;

    evt.preventDefault();
    const brandId = data?.sku?.productLine?.brandId;
    assertBrandId(brandId);
    setCurrentBrandId(brandId);
    const el = evt.target as HTMLAnchorElement;
    asyncVoid(router.push(el.href));
  };

  const skuComponentsByType = sku?.skuComponentsList
    ? keyBy(sku.skuComponentsList, "skuComponentType")
    : undefined;

  return (
    <>
      <PopoverArrow />
      <PopoverBody>
        <LabeledValueTable
          isLoaded={!loading}
          labelProps={{ width: "13em", textAlign: "right" }}
          sx={{ tableLayout: "fixed" }}
          width="100%"
        >
          {(isAdmin || sku?.productLine?.brandId !== currentBrandId) && (
            <LabeledValueRow
              label="Brand"
              value={sku?.productLine?.brand?.name}
            />
          )}

          <LabeledValueRow
            label="Product Line"
            value={
              sku?.productLine?.name &&
              (isAdmin ? (
                sku.productLine.name
              ) : (
                <Link
                  onClick={handleLinkClick}
                  href={selectedProductLine(sku.productLine.id)}
                >
                  {sku.productLine.name}
                </Link>
              ))
            }
          />

          <LabeledValueRow
            label="SKU"
            value={
              sku?.name &&
              (isAdmin ? (
                sku.name
              ) : (
                <Link onClick={handleLinkClick} href={selectedSku(sku.id)}>
                  {sku.name}
                </Link>
              ))
            }
          />

          <LabeledValueRow label="Short Name" value={sku?.fullNameShort} />

          <LabeledValueRow
            label="Unit Size"
            value={formatUnitNetWeight(
              sku?.unitNetWeight,
              sku?.unitNetWeightUnit,
            )}
          />

          <LabeledValueRow
            label="Case Quantity"
            value={formatInteger(sku?.productLine?.wholesaleCaseQuantity)}
          />

          {typeof sku?.productLine?.innerCaseQuantity === "number" && (
            <LabeledValueRow
              label="Inner Case Quantity"
              value={formatInteger(sku.productLine.innerCaseQuantity)}
            />
          )}

          <LabeledValueRow
            label="Internal SKU ID"
            value={
              sku?.internalSkuId && (
                <CopyableCode code={sku.internalSkuId} mr="-.5em" />
              )
            }
          />

          {SKU_COMPONENT_TYPES.map((sct) => {
            const skuComponent = skuComponentsByType?.[sct];
            return (
              <LabeledValueRow
                key={sct}
                label={`${SKU_COMPONENT_TYPE_LABELS[sct]} UPC`}
                value={
                  skuComponent?.upc && (
                    <CopyableCode
                      code={formatUpc(skuComponent.upc)}
                      mr="-.5em"
                    />
                  )
                }
              />
            );
          })}
        </LabeledValueTable>
      </PopoverBody>
    </>
  );
}
